import { useRef, useCallback, useMemo } from 'react'
import clientEnv from '../global/clientEnv'
import {
  type NewCookieEvent,
  type NewCookieEventHandler,
  type CookieListItem,
} from './cookieChange.types'
import { createNewCookieObserver, getCurrentCookies } from './cookieChange.utils'

const checkCookieInInventoryRegex = (cookieName: string, inventoryCookiesList: string[]) => {
  for (const regex of inventoryCookiesList) {
    if (new RegExp(regex).test(cookieName)) {
      return true
    }
  }
  return false
}

const checkKnownCookies = (knownCookiesList: CookieListItem[], cookie: CookieListItem) => {
  for (let x = 0; x < knownCookiesList.length; x++) {
    const knownCookie = knownCookiesList[x]
    if (knownCookie.name === cookie.name && knownCookie.domain === cookie.domain) {
      return true
    }
  }
  return false
}

export const useNewCookieDetection = (
  handler: NewCookieEventHandler
): (() => Promise<() => void>) => {
  const knownCookiesRef = useRef<CookieListItem[]>([])
  const observerCleanupRef = useRef<(() => void) | null>(null)
  const inventoryCookiesList = useMemo(() => {
    if (clientEnv.PUBLIC_CLIENT_KNOWN_COOKIE_NAMES) {
      return clientEnv.PUBLIC_CLIENT_KNOWN_COOKIE_NAMES.split(',')
    }
    return []
  }, [])

  const handleNewCookie = useCallback(
    (cookie: CookieListItem) => {
      if (checkKnownCookies(knownCookiesRef.current, cookie)) {
        return
      }

      knownCookiesRef.current.push(cookie)

      // Create a new cookie event and pass it to the handler
      handler({
        name: cookie.name,
        domain: cookie?.domain,
        timestamp: new Date(),
        isUnexpected: !checkCookieInInventoryRegex(cookie.name, inventoryCookiesList),
      })
    },
    [handler, inventoryCookiesList]
  )

  return useCallback(async () => {
    const currentCookies = await getCurrentCookies()
    knownCookiesRef.current = currentCookies

    // Perform an initial cookie scan
    knownCookiesRef.current.forEach(cookie => {
      if (!checkCookieInInventoryRegex(cookie.name, inventoryCookiesList)) {
        const event: NewCookieEvent = {
          name: cookie.name,
          domain: cookie?.domain,
          timestamp: new Date(),
          isUnexpected: true,
        }
        handler(event)
      }
    })

    // Create a new cookie observer to detect new cookies
    observerCleanupRef.current = createNewCookieObserver(handleNewCookie)

    return () => {
      if (observerCleanupRef.current) {
        observerCleanupRef.current()
        observerCleanupRef.current = null
      }
    }
  }, [handleNewCookie, inventoryCookiesList, handler])
}
